import { useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";

export interface ContextBuffer {
  usedTokens: number;
  freeTokens: number;
  totalTokens: number;
  usagePercent: number;
}

/**
 * Reads the context_buffer from the AG-UI agent state and exposes the
 * latest token usage as React state.
 *
 * The backend sets /context_buffer via a StateDeltaEvent at the end of
 * each run. The ag-ui client applies the JSON Patch internally and
 * fires onStateChanged, which this hook subscribes to.
 */
export function useContextBuffer(agent: HttpAgent): ContextBuffer | null {
  const [buffer, setBuffer] = useState<ContextBuffer | null>(null);

  useEffect(() => {
    const sub = agent.subscribe({
      onStateChanged: ({ state }) => {
        const cb = state?.context_buffer;
        if (!cb) return;
        const { used_tokens, free_tokens } = cb;
        if (typeof used_tokens !== "number" || typeof free_tokens !== "number") {
          return;
        }
        const total = used_tokens + free_tokens;
        setBuffer({
          usedTokens: used_tokens,
          freeTokens: free_tokens,
          totalTokens: total,
          usagePercent: total > 0 ? (used_tokens / total) * 100 : 0,
        });
      },
    });

    return () => sub.unsubscribe();
  }, [agent]);

  return buffer;
}
